import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Check, X, DownloadSimple, FileText, Paperclip } from "@phosphor-icons/react";
import { fmtDateIN, isoDateIST } from "../lib/utils";
import { toast } from "sonner";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const label = (c) => (c || "").replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
const d = (iso) => (iso ? fmtDateIN(isoDateIST(iso)) : "—");

function Row({ k, v, testid }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm border-b border-zinc-100 last:border-0">
      <span className="text-zinc-500">{k}</span><span className="text-right font-medium" data-testid={testid}>{v || "—"}</span>
    </div>
  );
}

function Receipt({ r, idx }) {
  const src = r.base64_data ? `data:${r.content_type};base64,${r.base64_data}` : null;
  const isImg = (r.content_type || "").startsWith("image/");
  if (!src) return <div className="text-xs text-zinc-500 flex items-center gap-1"><Paperclip size={12}/>{r.file_name}</div>;
  return isImg ? (
    <a href={src} target="_blank" rel="noreferrer" data-testid={`claim-receipt-${idx}`}>
      <img src={src} alt={r.file_name} className="h-28 w-28 object-cover rounded border border-zinc-200 hover:opacity-80"/>
    </a>
  ) : (
    <a href={src} download={r.file_name} className="flex items-center gap-1 text-xs text-blue-700 underline" data-testid={`claim-receipt-${idx}`}>
      <FileText size={14}/>{r.file_name}
    </a>
  );
}

function Items({ items }) {
  let n = 0;
  return (
    <div className="space-y-3" data-testid="claim-items">
      {items.map((it, i) => (
        <div key={i} className="rounded-md border border-zinc-200 p-3">
          <div className="flex justify-between text-sm">
            <span className="font-medium">{label(it.category)}</span>
            <span className="tabular-nums font-semibold">{inr(it.amount)}</span>
          </div>
          <div className="text-xs text-zinc-500 mt-0.5">{fmtDateIN(it.expense_date)}{it.description ? ` · ${it.description}` : ""}</div>
          {it.receipts?.length > 0 && <div className="flex flex-wrap gap-2 mt-2">{it.receipts.map((r) => <Receipt key={n} r={r} idx={n++}/>)}</div>}
        </div>
      ))}
    </div>
  );
}

const MODE = { cash: "Cash", upi: "UPI", bank_transfer: "Bank transfer", neft: "NEFT", cheque: "Cheque" };

function Payment({ p }) {
  return (
    <div className="mt-6 rounded-md border border-blue-200 bg-blue-50/50 p-3" data-testid="claim-drawer-payment">
      <h4 className="text-xs uppercase tracking-wide text-blue-800 mb-1">Payment</h4>
      <Row k="Paid by" v={MODE[p.mode] || p.mode}/>
      <Row k="Paid on" v={fmtDateIN(p.paid_on)}/>
      <Row k="Reference" v={p.reference}/>
      <Row k="Paid to" v={p.paid_to}/>
      {p.claim_count > 1 && <Row k="Consolidated" v={`${p.claim_count} claims · ${inr(p.total_amount)} in one payment`}/>}
      <Row k="Recorded by" v={p.recorded_by_name}/>
      {p.notes && <Row k="Notes" v={p.notes}/>}
    </div>
  );
}

export function ExpenseClaimDrawer({ claimId, onClose, canApprove, canFinance, onDecide, onVoucher, onPay, onPayroll, onUnpayroll }) {
  const [c, setC] = useState(null);
  useEffect(() => {
    setC(null);
    if (!claimId) return;
    api.get(`/expenses/${claimId}`).then((r) => setC(r.data)).catch((e) => toast.error(formatApiError(e?.response?.data?.detail)));
  }, [claimId]);
  const st = c?.status;
  return (
    <Sheet open={!!claimId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="claim-drawer">
        {!c ? <div className="text-sm text-zinc-500 p-4">Loading…</div> : (<>
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2" data-testid="claim-drawer-title">{c.title} <Badge variant="outline" className="uppercase text-[10px]" data-testid="claim-drawer-status">{st}</Badge></SheetTitle>
            <SheetDescription>{c.purpose || "No purpose given"}</SheetDescription>
          </SheetHeader>
          <div className="text-3xl font-semibold tabular-nums mt-4" data-testid="claim-drawer-total">{inr(c.total_amount)}</div>
          <div className="mt-4">
            <Row k="Employee" v={c.employee_name} testid="claim-drawer-employee"/>
            <Row k="Project / cost code" v={c.project_code}/>
            <Row k="Created" v={d(c.created_at)}/>
            <Row k="Submitted" v={d(c.submitted_at)}/>
            {(st === "approved" || st === "reimbursed" || st === "rejected") && <Row k={st === "rejected" ? "Rejected" : "Approved"} v={`${d(c.decided_at)}${c.decided_by_name ? ` · ${c.decided_by_name}` : ""}`}/>}
            {st === "rejected" && <Row k="Rejection reason" v={c.rejection_reason} testid="claim-drawer-reason"/>}
            {c.reimbursed_at && <Row k="Reimbursed" v={d(c.reimbursed_at)}/>}
            {c.pushed_to_payroll_run_id && <Row k="Payroll" v="Added to payroll run"/>}
            {c.fraud && <Row k="Risk" v={`${label(c.fraud.tier || c.fraud.level)}${typeof c.fraud.risk_score === "number" ? ` (${c.fraud.risk_score}/100)` : ""} · ${c.fraud.signals?.length || 0} signal(s)`}/>}
            {c.budget_check?.message && <Row k="Budget" v={c.budget_check.message}/>}
          </div>
          {c.payment && <Payment p={c.payment}/>}
          {!c.payment && c.reimbursed_method && <Row k="Reimbursed via" v={MODE[c.reimbursed_method] || c.reimbursed_method}/>}
          <h4 className="text-xs uppercase tracking-wide text-zinc-500 mt-6 mb-2">Items ({c.items?.length || 0})</h4>
          <Items items={c.items || []}/>
          <div className="flex gap-2 mt-6">
            {canApprove && st === "submitted" && (<>
              <Button onClick={() => onDecide(c.id, "approve")} className="gap-1" data-testid="claim-drawer-approve"><Check size={13}/> Approve</Button>
              <Button variant="outline" onClick={() => onDecide(c.id, "reject")} className="gap-1" data-testid="claim-drawer-reject"><X size={13}/> Reject</Button>
              {canFinance && <Button variant="outline" onClick={() => onPay(c)} data-testid="claim-drawer-approve-pay">Approve & pay now</Button>}
            </>)}
            {canFinance && st === "approved" && !c.pushed_to_payroll_run_id && (<>
              <Button onClick={() => onPay(c)} data-testid="claim-drawer-pay">Record payment</Button>
              <Button variant="outline" onClick={() => onPayroll(c.id)} data-testid="claim-drawer-payroll">Pay via payroll</Button>
            </>)}
            {canFinance && st === "approved" && c.pushed_to_payroll_run_id && (
              <Button variant="outline" onClick={() => onUnpayroll(c.id)} data-testid="claim-drawer-unpayroll">Undo payroll</Button>
            )}
            {(st === "approved" || st === "reimbursed") && (
              <Button variant="outline" onClick={() => onVoucher(c.id)} className="gap-1" data-testid="claim-drawer-voucher"><DownloadSimple size={13}/> Voucher PDF</Button>
            )}
          </div>
        </>)}
      </SheetContent>
    </Sheet>
  );
}
